import { createBrowserClient, createServerClient } from "@supabase/ssr"

import { buildOAuthRedirectTo } from "../../auth/oauthRedirect.js"
import { getSupabaseAuthConfigFromEnv } from "../../platformConfig.js"

import type {
  AppSupabaseClient,
  AuthEnv,
  AuthCodeExchangeResult,
  AuthProvider,
  AuthSignInResult,
  ExchangeCodeForSessionParams,
  GoogleSignInParams,
  SupabaseServerClientOptions,
} from "./types"

type BrowserClientFactory = <Client = AppSupabaseClient>(
  url: string,
  anonKey: string
) => Client

type ServerClientFactory = <Client = AppSupabaseClient>(
  url: string,
  anonKey: string,
  options: SupabaseServerClientOptions
) => Client

type CreateSupabaseAuthProviderOptions = {
  env?: AuthEnv
  createBrowserClientImpl?: BrowserClientFactory
  createServerClientImpl?: ServerClientFactory
}

export function createSupabaseAuthProvider({
  env,
  createBrowserClientImpl = createBrowserClient as unknown as BrowserClientFactory,
  createServerClientImpl = createServerClient as unknown as ServerClientFactory,
}: CreateSupabaseAuthProviderOptions = {}): AuthProvider {
  const clientConfig = getSupabaseAuthConfigFromEnv(
    env as Parameters<typeof getSupabaseAuthConfigFromEnv>[0]
  )

  return {
    getClientConfig() {
      return clientConfig
    },
    createBrowserClient<Client = AppSupabaseClient>() {
      return createBrowserClientImpl<Client>(
        clientConfig.url,
        clientConfig.anonKey
      )
    },
    createServerClient<Client = AppSupabaseClient>(
      options: SupabaseServerClientOptions
    ) {
      return createServerClientImpl<Client>(
        clientConfig.url,
        clientConfig.anonKey,
        options
      )
    },
    async signInWithGoogle({
      client,
      origin,
      next,
    }: GoogleSignInParams): Promise<AuthSignInResult> {
      const result = await client.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: buildOAuthRedirectTo({
            origin,
            next: next ?? undefined,
          }),
        },
      })

      return {
        redirectUrl: result.data?.url ?? null,
        error: result.error,
      }
    },
    async exchangeCodeForSession({
      client,
      code,
    }: ExchangeCodeForSessionParams): Promise<AuthCodeExchangeResult> {
      const result = await client.auth.exchangeCodeForSession(code)

      return {
        accessToken: result.data?.session?.access_token ?? null,
        error: result.error,
      }
    },
  }
}

export function getAuthProvider(
  options?: CreateSupabaseAuthProviderOptions
): AuthProvider {
  return createSupabaseAuthProvider(options)
}
